import React, { useEffect, useRef, useState } from 'react';
import { DropdownSearch } from './DropdownSearch';
import { useResponsive } from '../hook/useResponsive';

interface Product {
    productId: string;
    sku?: string;
    groupName?: string;
    productName?: string;
    uomName?: string;
    checkProduct?: boolean;
    productUOMs?: any[];
    productUOMId: string;
    cost: string;
    productPrices: any[];
    originalOrderDetailId: string
}

interface ProductSelectionSupplierModalProps {
    showPopup: boolean;
    searchUOMBase: any;
    onClose: () => void;
    products: Product[];
    filter: {
        searchValue: string;
    };
    onFilterChange: (filter: { searchValue: string }) => void;
    onSearchSubmit: (e: any) => void;
    onSelectProduct: (value: Product) => void;
    onSelectUOM: (value: Product[]) => void;
    onCancel: () => void;
    onSave: (value: Product[]) => void;
    loading?: boolean;
    language?: Record<string, string>;
    scrollRef?: React.RefObject<HTMLDivElement>;
    loaderRef?: (node: HTMLDivElement | null) => void;
    SearchIcon: React.ComponentType;
    CloseIcon: React.ComponentType;
    WhiteCheckIcon: React.ComponentType;
}

const ProductSelectionSupplierModal: React.FC<ProductSelectionSupplierModalProps> = ({
    showPopup,
    onClose,
    searchUOMBase,
    products,
    filter,
    onFilterChange,
    onSearchSubmit,
    onSelectProduct,
    onSelectUOM,
    onCancel,
    onSave,
    loading = false,
    language,
    scrollRef,
    loaderRef,
    SearchIcon,
    CloseIcon,
    WhiteCheckIcon,
}) => {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownUOMRef = useRef<HTMLDivElement>(null);
    const [isUOMDropdownOpen, setIsUOMDropdownOpen] = useState<{
        [key: string]: boolean;
    }>({});
    const [listUOM, setListUOM] = useState<any>([]);
    const [searchUOM, setSearchUOM] = useState<any>();

    useEffect(() => {
        setSearchUOM(searchUOMBase);
    }, [searchUOMBase]);
    const handleUOMSelect = (uom: any, productId: string) => {
        const productSelect: any = products.find((v: any) => v.productId === productId);
        let uomSelect: any;
        if (productSelect) {
            uomSelect = productSelect?.productUOMs.find((v: any) => v?.id === uom?.id);
        }
        setSearchUOM((prev: any) => ({
            ...prev,
            [productId]: uomSelect?.uom?.uomName || uom.uomName,
        }));
        const updatedList: any = products?.map((item) =>
            item.productId === productId
                ? {
                    ...item,
                    productUOMId: uomSelect.id,
                }
                : item
        );
        onSelectUOM(updatedList);
        setIsUOMDropdownOpen((prev) => ({ ...prev, [productId]: false }));
    };
    const handleSearchUOM = (value: string, index: number) => {
    };
    const { isMobile } = useResponsive();

    const renderRowDesktop = (value: Product, idx: number) => (
        <div
            key={idx}
            className="flex items-center h-10 border-b border-[#F0F0F0]"
            style={{
                padding: '0px 16px',
                borderBottom: '1px solid #DBE4E4',
                borderLeft: '1px solid #DBE4E4',
                borderRight: '1px solid #DBE4E4',
                borderBottomLeftRadius: idx === (products?.length ?? 0) - 1 ? '12px' : '',
                borderBottomRightRadius: idx === (products?.length ?? 0) - 1 ? '12px' : '',
            }}
        >
            <div
                style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                className="text-[14px] font-inter"
            >
                {value.sku || '105213242'}
            </div>
            <div
                style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                className="text-[14px] font-inter"
            >
                {value.groupName}
            </div>
            <div
                style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                className="text-[14px] font-inter"
            >
                {value.productName || 'Product name'}
            </div>
            <div
                style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                className="text-[14px] font-inter"
            >
                <DropdownSearch<{ id: string; uomName: string }>
                    refKey={`UOMBase-${value.productId}`}
                    refs={{
                        [`UOMBase-${value.productId}`]: dropdownUOMRef,
                    }}
                    openStates={{
                        [`UOMBase-${value.productId}`]: isUOMDropdownOpen[value?.productId],
                    }}
                    open={() => {
                        setIsUOMDropdownOpen((prev) => ({
                            ...prev,
                            [value.productId]: true,
                        }));
                        setListUOM(
                            value?.productUOMs?.map((v: any) => ({
                                id: v.id,
                                productId: v.productId,
                                uomName: v.uom.uomName,
                            }))
                        );
                    }}
                    close={() =>
                        setIsUOMDropdownOpen((prev) => ({
                            ...prev,
                            [value.productId]: false,
                        }))
                    }
                    data={listUOM}
                    labelExtractor={(item) => item.uomName}
                    value={searchUOM?.[value?.productId] ?? ""}
                    setValue={(val) =>
                        setSearchUOM((prev: any) => ({
                            ...prev,
                            [value.productId]: val,
                        }))
                    }
                    onSelect={(uom) => handleUOMSelect(uom, value.productId)}
                    onSearch={() => {
                        handleSearchUOM('', idx);
                    }}
                    tabIndex={8}
                    borderNone={true}
                    inItem={true}
                />
            </div>
            <div style={{ width: '80px' }} className="flex items-center justify-center">
                <div
                    className="cursor-pointer w-5 h-5 flex items-center justify-center"
                    onClick={() => onSelectProduct(value)}
                    style={{
                        border: value?.checkProduct ? '' : '1px solid #C9D7D8',
                        backgroundColor: value?.checkProduct ? '#0F7679' : 'transparent',
                        borderRadius: '6px',
                    }}
                >
                    {value?.checkProduct ? <WhiteCheckIcon /> : null}
                </div>
            </div>
        </div>
    );

    const renderRowMobile = (value: Product, idx: number) => (
        <div
            key={idx}
            style={{
                border: '1px solid #DBE4E4',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: idx === (products?.length ?? 0) - 1 ? '' : '12px',
                backgroundColor: '#FFFFFF',
            }}
        >
            <div className="flex items-center justify-between">
                <div className="text-[14px] font-medium leading-[18px] font-inter" style={{ color: '#4A5353' }}>
                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>{language?.['SKU'] || 'SKU'}</p>
                    {value.sku || '105213242'}
                </div>
                <div className="flex items-center justify-center">
                    <div
                        className="cursor-pointer w-5 h-5 flex items-center justify-center"
                        onClick={() => onSelectProduct(value)}
                        style={{
                            border: value?.checkProduct ? '' : '1px solid #C9D7D8',
                            backgroundColor: value?.checkProduct ? '#0F7679' : 'transparent',
                            borderRadius: '6px',
                        }}
                    >
                        {value?.checkProduct ? <WhiteCheckIcon /> : null}
                    </div>
                </div>
            </div>
            <div className="flex items-center" style={{ margin: '12px 0' }}>
                <div className="text-[14px] font-medium leading-[18px] font-inter" style={{ color: '#4A5353', width: '50%' }}>
                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>{language?.['Group'] || 'Group'}</p>
                    {value.groupName}
                </div>
                <div className="text-[14px] font-medium leading-[18px] font-inter" style={{ color: '#4A5353', width: '50%' }}>
                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>{language?.['Product'] || 'Product'}</p>
                    {value.productName || 'Product name'}
                </div>
            </div>
            <div className="text-[14px] font-medium leading-[18px] font-inter w-full" style={{ color: '#4A5353' }}>
                <p style={{ color: '#6A7878', marginBottom: '4px' }}>{language?.['UOM'] || 'UOM'}</p>
                <DropdownSearch<{ id: string; uomName: string }>
                    refKey={`UOMBase-${value.productId}`}
                    refs={{
                        [`UOMBase-${value.productId}`]: dropdownUOMRef,
                    }}
                    openStates={{
                        [`UOMBase-${value.productId}`]: isUOMDropdownOpen[value?.productId],
                    }}
                    open={() => {
                        setIsUOMDropdownOpen((prev) => ({
                            ...prev,
                            [value.productId]: true,
                        }));
                        setListUOM(
                            value?.productUOMs?.map((v: any) => ({
                                id: v.id,
                                productId: v.productId,
                                uomName: v.uom.uomName,
                            }))
                        );
                    }}
                    close={() =>
                        setIsUOMDropdownOpen((prev) => ({
                            ...prev,
                            [value.productId]: false,
                        }))
                    }
                    data={listUOM}
                    labelExtractor={(item) => item.uomName}
                    value={searchUOM?.[value?.productId] ?? ""}
                    setValue={(val) =>
                        setSearchUOM((prev: any) => ({
                            ...prev,
                            [value.productId]: val,
                        }))
                    }
                    onSelect={(uom) => handleUOMSelect(uom, value.productId)}
                    onSearch={() => handleSearchUOM('', idx)}
                    tabIndex={8}
                    borderNone={false}
                    inItem={true}
                />
            </div>
        </div>
    );

    return (
        <div
            className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopup ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                } transition-opacity duration-300 ease-in-out`}
            style={{
                backgroundColor: '#22252552',
                zIndex: '200',
                transition: 'opacity 0.3s ease, visibility 0.3s ease',
                width: '100%',
                height: '100vh',
            }}
        >
            <div
                className={`transform transition-transform duration-300 ease-in-out ${showPopup ? 'translate-y-0' : 'translate-y-4 opacity-0'
                    }`}
                style={{
                    width: isMobile ? 'calc(100% - 16px)' : '80%',
                    maxWidth: isMobile ? '100%' : undefined,
                    transition: 'transform 0.3s ease, opacity 0.3s ease',
                    height: isMobile ? 'min(92vh, 900px)' : 'calc(90vh - 100px)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        backgroundColor: 'white',
                        padding: isMobile ? '16px' : '24px',
                        width: '100%',
                        height: isMobile ? 'calc(100% - 77px)' : 'calc(100% - 93px)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    {/* Header */}
                    <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
                        <div className="flex items-start justify-between w-full gap-3">
                            <div
                                className="font-inter font-medium"
                                style={{ fontSize: '20px', color: '#222525' }}
                            >
                                {language?.['Return to supplier'] || 'Return to supplier'}
                            </div>
                            <div
                                onClick={onClose}
                                className="flex items-center justify-center cursor-pointer shrink-0"
                                style={{ width: '24px', height: '24px' }}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                        <div className={`${isMobile ? 'w-full' : 'flex'}`} style={{ alignItems: 'start', gap: isMobile ? 0 : '34px' }}>
                            <div className={`relative h-10 ${isMobile ? 'w-full' : 'w-[400px]'}`}>
                                <input
                                    ref={searchInputRef}
                                    style={{
                                        border: '1px solid #C9D7D8',
                                        padding: '8px 36px',
                                        minWidth: isMobile ? 0 : 350,
                                        width: '100%',
                                        height: '40px',
                                        color: '#05292A',
                                        borderRadius: '12px',
                                    }}
                                    name="searchValue"
                                    onKeyDown={onSearchSubmit}
                                    value={filter.searchValue}
                                    onChange={(e) => onFilterChange({ ...filter, searchValue: e.target.value })}
                                    className="font-inter placeholder:text-placeholder text-main p-3 outline-none focus-none bg-transparent w-full"
                                />
                                <div
                                    className="absolute top-1/2 -translate-y-1/2"
                                    style={{ left: '8px' }}
                                >
                                    <SearchIcon />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table / cards */}
                    <div style={{ marginTop: '20px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {!isMobile && (
                        <div
                            className="w-full flex items-center h-10 shrink-0"
                            style={{
                                borderTopLeftRadius: '12px',
                                borderTopRightRadius: '12px',
                                padding: '0px 16px',
                                backgroundColor: '#EBF0F0',
                                borderLeft: '1px solid #DBE4E4',
                                borderRight: '1px solid #DBE4E4',
                                borderTop: '1px solid #DBE4E4',
                            }}
                        >
                            {['SKU', 'Group', 'Product', 'UOM', 'Actions'].map((header, idx) => (
                                <div
                                    key={header}
                                    className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                    style={{
                                        color: '#6A7878',
                                        width: idx === 4 ? '80px' : 'calc((100% - 80px) / 4)',
                                        justifyContent: idx === 4 ? 'center' : 'flex-start',
                                    }}
                                >
                                    {header}
                                </div>
                            ))}
                        </div>
                        )}

                        <div
                            className={`rounded-b-md w-full ${isMobile ? 'overflow-y-auto flex-1' : 'scroll-container'}`}
                            style={{
                                height: isMobile ? undefined : 'calc(100% - 40px)',
                                overflowY: isMobile ? 'auto' : 'auto',
                                backgroundColor: '#FFFFFF',
                            }}
                            ref={scrollRef}
                        >
                            {products && products.map((value, idx) => (isMobile ? renderRowMobile(value, idx) : renderRowDesktop(value, idx)))}

                            {loaderRef && (
                                <div
                                    ref={loaderRef}
                                    style={{
                                        height: '20px',
                                        width: '100%',
                                        display: 'block'
                                    }}
                                >
                                    {loading && (
                                        <div
                                            className="w-full flex items-center justify-center"
                                            style={{ padding: '20px 0' }}
                                        >
                                            <span className="loader"></span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div
                    className="flex items-center justify-end"
                    style={{
                        gap: '12px',
                        borderBottomLeftRadius: '16px',
                        borderBottomRightRadius: '16px',
                        padding: '24px',
                        backgroundColor: '#F8F8F8',
                        border: '1px solid #DBE4E4',
                        width: '100%',
                    }}
                >
                    <div
                        onClick={onCancel}
                        className="font-inter cursor-pointer"
                        style={{
                            padding: '10px 24px',
                            borderRadius: '30px',
                            backgroundColor: '#FFFFFF',
                            color: '#222525',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: '1px solid #C9D7D8',
                        }}
                    >
                        {language?.['Cancel'] || 'Cancel'}
                    </div>
                    <div
                        onClick={() => onSave(products ?? [])}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '30px',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: '#0F7679',
                        }}
                        className="font-inter cursor-pointer"
                    >
                        {language?.['Return Now'] || 'Return Now'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductSelectionSupplierModal;